import { Breadcrumbs, Card, Grid, Typography } from "@material-ui/core";
import React, { Children, isValidElement, useCallback, useState } from "react";
import {
  Button,
  CardContentInner,
  FormInput,
  Link,
  Loading,
  submitErrorsMutators,
  Title,
  useNotify,
  useTranslate,
} from "react-admin";
import { RouteComponentProps } from "react-router-dom";
import { Form } from "react-final-form";
import { CompactFormToolbar } from "../../common/form/CompactFormToolbar";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { ReportApiApiFactory } from "../../../common/data/service-proxies";
import { AxiosPromise } from "axios";
import "./ReportForm.css";
import { useReportContext } from "../../common/reports/useReportContext";
import arrayMutators from "final-form-arrays";

interface Props extends RouteComponentProps {
  reportName: string;
  title: string;
  children: React.ReactNode;
  transform?: (input: any) => any;
  submitMethod: (
    apiFactory: IReportApiFactory
  ) => (dailyLogReportInput?: any, options?: any) => AxiosPromise<void>;
}

interface BreadcrumbProps {
  reportName: string;
}

interface IReportApiFactory {
  reportDailylogreportPost(
    dailyLogReportInput?: any,
    options?: any
  ): AxiosPromise<void>;
  reportDailylogsreportPost(
    dailyLogsReportInput?: any,
    options?: any
  ): AxiosPromise<void>;
  reportEldeventsreportPost(
    eldEventsReportInput?: any,
    options?: any
  ): AxiosPromise<void>;
  reportIftamileagereportPost(
    iftaMileageReportInputDto?: any,
    options?: any
  ): AxiosPromise<void>;
  reportLocationhistoryreportPost(
    vehicleDistanceReportDto?: any,
    options?: any
  ): AxiosPromise<void>;
  reportVehicledistancereport2Post(
    vehicleDistanceReportDto?: any,
    options?: any
  ): AxiosPromise<void>;
  reportVehicledistancereportPost(
    vehicleDistanceReportDto?: any,
    options?: any
  ): AxiosPromise<void>;
  reportVehicleinactivityreportPost(
    vehicleInactivityReportInput?: any,
    options?: any
  ): AxiosPromise<void>;
  reportVehiclemileagereportPost(
    vehicleMileageReportInputDto?: any,
    options?: any
  ): AxiosPromise<void>;
  reportIftasummaryreportPost(
    vehicleMileageReportInputDto?: any,
    options?: any
  ): AxiosPromise<void>;
  reportVehiclemileagesummaryreportPost(
    vehicleMileageReportInputDto?: any,
    options?: any
  ): AxiosPromise<void>;
  reportDvirinspectionsreportPost(
    input?: any,
    options?: any
  ): AxiosPromise<void>;
  reportTripreportdetailPost(
    tripReportDetailInputDto?: any,
    options?: any
  ): AxiosPromise<void>;
  reportTripdetailPost(
    tripDetailInputDto?: any,
    options?: any
  ): AxiosPromise<void>;
  reportIftamileagereportbasicPost(
    tripDetailInputDto?: any,
    options?: any
  ): AxiosPromise<void>;
}

const defaultMutators = {
  ...arrayMutators,
  ...submitErrorsMutators,
};

const Breadcrumb = ({ reportName }: BreadcrumbProps) => {
  const translate = useTranslate();
  return (
    <Breadcrumbs aria-label="breadcrumb">
      <Link to="/reports">
        <Typography variant="h6">{translate("menu.reports")}</Typography>
      </Link>
      <Typography variant="h6">
        {translate(reportName, { _: reportName })}
      </Typography>
    </Breadcrumbs>
  );
};

export const ReportForm = ({
  title,
  history,
  children,
  submitMethod,
  reportName,
  transform = (x) => x,
}: Props) => {
  const axios = useAxiosInstance();
  const [loading, setLoading] = useState<boolean>(false);
  const notify = useNotify();

  useReportContext({ reportName: reportName, onDownloading: setLoading });
  const handleFormSubmit = useCallback(
    (values: any) => {
      if (!axios) {
        return;
      }

      setFormValues(values);

      const method = submitMethod(
        ReportApiApiFactory(undefined, axios.defaults.baseURL, axios)
      );

      setLoading(true);
      method({ ...transform(values), reportName })
        .then(() => {})
        .catch((e) => {
          notify("resources.reports.labels.error", "error");
          console.error(e);
        });
    },
    [axios, submitMethod, notify, reportName, transform]
  );

  const record = { id: 0 };

  const [formValues, setFormValues] = useState(record);

  return (
    <Card>
      <Title title={<Breadcrumb reportName={title} />} />
      {loading ? (
        <Grid container>
          <Grid
            item
            xl={12}
            lg={12}
            md={12}
            sm={12}
            xs={12}
            className="loading-wrapper"
          >
            <Loading />
          </Grid>
        </Grid>
      ) : (
        <Form
          onSubmit={handleFormSubmit}
          mutators={defaultMutators}
          initialValues={formValues}
          render={({ handleSubmit }) => {
            return (
              <div className="simple-form form-create-edit">
                <form onSubmit={handleSubmit}>
                  <CardContentInner>
                    {Children.map(children, (child) => {
                      if (!isValidElement(child)) {
                        return null;
                      }

                      return (
                        <FormInput
                          basePath=""
                          input={child}
                          record={record}
                          resource=""
                          variant="outlined"
                        />
                      );
                    })}
                  </CardContentInner>
                  <CompactFormToolbar>
                    <Button
                      variant="contained"
                      type="submit"
                      color="primary"
                      label="resources.common.labels.download"
                    >
                      <GetAppIcon />
                    </Button>
                  </CompactFormToolbar>
                </form>
              </div>
            );
          }}
        />
      )}
    </Card>
  );
};
